import React from "react";

type Props = {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  className?: string;
};

const BASE =
  "inline-flex min-w-9 items-center justify-center rounded-lg px-2.5 py-1.5 text-sm transition-colors";

/**
 * Compact pager. Collapses to first/last + a window around the current page
 * once there are more than seven, so a long result set does not render a
 * hundred buttons the way the Bootstrap version did.
 */
export function Pagination({ page, totalPages, onChange, className = "" }: Props) {
  if (totalPages <= 1) return null;

  const pages: Array<number | "gap"> = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i += 1) pages.push(i);
  } else {
    const window = [page - 1, page, page + 1].filter(
      (p) => p > 1 && p < totalPages
    );
    pages.push(1);
    if (window[0] > 2) pages.push("gap");
    pages.push(...window);
    if (window[window.length - 1] < totalPages - 1) pages.push("gap");
    pages.push(totalPages);
  }

  return (
    <nav aria-label="Pagination" className={`flex items-center gap-1 ${className}`}>
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
        className={`${BASE} text-zinc-500 hover:bg-elevated hover:text-zinc-900 disabled:pointer-events-none disabled:opacity-40`}
      >
        <i className="bi bi-chevron-left text-xs" />
      </button>

      {pages.map((entry, index) =>
        entry === "gap" ? (
          <span key={`gap-${index}`} className="px-1 text-sm text-zinc-400">
            …
          </span>
        ) : (
          <button
            key={entry}
            type="button"
            onClick={() => onChange(entry)}
            aria-current={entry === page ? "page" : undefined}
            className={`${BASE} ${
              entry === page
                ? "bg-brand/10 text-brand-strong font-medium"
                : "text-zinc-600 hover:bg-elevated hover:text-zinc-900"
            }`}
          >
            {entry}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
        aria-label="Next page"
        className={`${BASE} text-zinc-500 hover:bg-elevated hover:text-zinc-900 disabled:pointer-events-none disabled:opacity-40`}
      >
        <i className="bi bi-chevron-right text-xs" />
      </button>
    </nav>
  );
}
